import { useRef, useState, useEffect, useCallback } from 'react';
import i18next from 'i18next';
import { trackEvent } from '../../utils/analytics';

// Fallback for legacy data that has no explicit `media` field.
const detectPlatform = (url = '') => {
  const u = url.toLowerCase();
  if (u.includes('youtube.com') || u.includes('youtu.be')) return 'youtube';
  if (u.includes('instagram.com')) return 'instagram';
  if (u.includes('facebook.com') || u.includes('fb.watch')) return 'facebook';
  return null;
};

// Inline Instagram glyph so we don't have to ship a new binary asset.
const InstagramBadge = () => (
  <svg className="video-card__platform" viewBox="0 0 24 24" aria-label="instagram" role="img">
    <defs>
      <radialGradient id="ig-g" cx="30%" cy="100%" r="110%">
        <stop offset="0%" stopColor="#fdf497" />
        <stop offset="25%" stopColor="#fdf497" />
        <stop offset="50%" stopColor="#fd5949" />
        <stop offset="75%" stopColor="#d6249f" />
        <stop offset="100%" stopColor="#285AEB" />
      </radialGradient>
    </defs>
    <rect x="1.5" y="1.5" width="21" height="21" rx="6" fill="url(#ig-g)" />
    <rect x="5.5" y="5.5" width="13" height="13" rx="4" fill="none" stroke="#fff" strokeWidth="1.8" />
    <circle cx="12" cy="12" r="3.2" fill="none" stroke="#fff" strokeWidth="1.8" />
    <circle cx="17" cy="7" r="1.2" fill="#fff" />
  </svg>
);

const PlatformBadge = ({ media }) => {
  if (media === 'instagram') return <InstagramBadge />;
  if (media === 'youtube') return <img className="video-card__platform" src="img/youtubelogo.png" alt="youtube" />;
  if (media === 'facebook') return <img className="video-card__platform" src="img/fblogo.png" alt="facebook" />;
  return null;
};

const VideoCard = ({ item }) => {
  const media = item.media || detectPlatform(item.url);
  return (
    <a
      className="video-card"
      href={item.url}
      target="_blank"
      rel="noreferrer"
      onClick={() => trackEvent('video_click', { url: item.url, title: item.title, media, location: 'video_section' })}
    >
      <div className="video-card__media">
        {item.img ? (
          <img src={item.img} alt={item.title} loading="lazy" />
        ) : (
          <div className="video-card__media-fallback" />
        )}
        <PlatformBadge media={media} />
        {item.important && <span className="video-card__badge">{i18next.t('热门')}</span>}
      </div>
      <h3 className="video-card__title">{item.title}</h3>
    </a>
  );
};

const VideoSection = ({ videos = [] }) => {
  const scrollerRef = useRef(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const count = Array.isArray(videos) ? videos.length : 0;

  const updateArrows = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 1);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;
    updateArrows();
    el.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', updateArrows);
    return () => {
      el.removeEventListener('scroll', updateArrows);
      window.removeEventListener('resize', updateArrows);
    };
  }, [updateArrows, count]);

  const scrollByPage = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: 'smooth' });
  };

  if (count === 0) return null;

  const sorted = [...videos].sort((a, b) => Number(!!b.important) - Number(!!a.important));

  return (
    <section className="video-section" aria-labelledby="video-section-heading">
      <h2 id="video-section-heading" className="section-title">
        {i18next.t('选情视频')}
      </h2>
      <div className="video-section__viewport">
        <button
          type="button"
          className="video-section__nav video-section__nav--prev"
          onClick={() => scrollByPage(-1)}
          disabled={!canPrev}
          aria-label={i18next.t('上一个')}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <polyline points="15,5 8,12 15,19" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <div className="video-section__scroller" ref={scrollerRef}>
          {sorted.map((v, i) => (
            <div key={i} className="video-section__item">
              <VideoCard item={v} />
            </div>
          ))}
        </div>
        <button
          type="button"
          className="video-section__nav video-section__nav--next"
          onClick={() => scrollByPage(1)}
          disabled={!canNext}
          aria-label={i18next.t('下一个')}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <polyline points="9,5 16,12 9,19" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
    </section>
  );
};

export default VideoSection;
